import { Injectable, computed, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of, tap, catchError, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ExternalUser, InvitationPreview, StudioTokens } from '../models/auth.models';
import { TokenStorageService } from './token-storage.service';

interface RequestOtpBody {
  email: string;
  invitationToken?: string;
  preferredLanguage?: string;
}

interface VerifyOtpBody {
  email: string;
  code: string;
  invitationToken?: string;
  displayName?: string;
  preferredLanguage?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly base = environment.studioAuthBase;
  private readonly user = signal<ExternalUser | null>(null);
  readonly currentUser = this.user.asReadonly();
  readonly isAuthenticated = computed(() => this.user() !== null);

  constructor(
    private http: HttpClient,
    private tokenStorage: TokenStorageService,
    private router: Router
  ) {
    // Rehydrate the signal from localStorage AFTER tokenStorage is injected.
    this.user.set(this.tokenStorage.getUser());
  }

  getInvitation(token: string): Observable<InvitationPreview> {
    return this.http.get<InvitationPreview>(`${this.base}/invitation/${encodeURIComponent(token)}`);
  }

  requestOtp(body: RequestOtpBody): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.base}/request-otp`, body);
  }

  verifyOtp(body: VerifyOtpBody): Observable<StudioTokens> {
    return this.http.post<StudioTokens>(`${this.base}/verify-otp`, body).pipe(
      tap(res => this.storeSession(res))
    );
  }

  refresh(): Observable<{ accessToken: string; refreshToken: string; accessTokenExpiresAt: string; refreshTokenExpiresAt: string }> {
    const refreshToken = this.tokenStorage.getRefreshToken();
    if (!refreshToken) return throwError(() => new Error('no refresh token'));
    return this.http.post<{ accessToken: string; refreshToken: string; accessTokenExpiresAt: string; refreshTokenExpiresAt: string }>(
      `${this.base}/refresh`,
      { refreshToken }
    ).pipe(
      tap(res => {
        this.tokenStorage.setAccessToken(res.accessToken, res.accessTokenExpiresAt);
        this.tokenStorage.setRefreshToken(res.refreshToken);
      })
    );
  }

  logout(): Observable<unknown> {
    const refreshToken = this.tokenStorage.getRefreshToken();
    const req$ = refreshToken
      ? this.http.post(`${this.base}/logout`, { refreshToken }).pipe(catchError(() => of(null)))
      : of(null);

    return req$.pipe(
      tap(() => {
        this.tokenStorage.clear();
        this.user.set(null);
        this.router.navigate(['/login']);
      })
    );
  }

  /**
   * Sync the locally cached user (e.g. after profile update).
   */
  updateCachedUser(user: ExternalUser): void {
    this.tokenStorage.setUser(user);
    this.user.set(user);
  }

  private storeSession(res: StudioTokens): void {
    this.tokenStorage.setTokens(res.accessToken, res.refreshToken, res.accessTokenExpiresAt, res.user);
    this.user.set(res.user);
  }
}
